/**
 * Route table — the single source of truth for URL → page mapping
 * (routes are registered only here, nowhere else).
 *
 * Tree shape:
 *   - `/`               App shell (Nav + footer); every child page is
 *                       wrapped in an auth gate. `<AuthProvider>` sits
 *                       above `<App>` so the shell itself can read auth.
 *   - `/login`, `/logout`  standalone pages, outside the shell.
 *
 * Auth gates:
 *   - `<RequireAuth>`                              any signed-in user.
 *   - `<RequireFlag flag="isFulcrumInternal">`     `mars-live` only.
 *   - `<RequireFlag flag="isAnalyticsAdmin">`      `admin/*` only.
 *
 * A few pages intentionally answer to several URLs: EconomicOutlookPage
 * at `/economic-outlook` + `/econ-activity`, and MarsForecastsRoute at
 * `/forecasts`, `/forecasts/:tab`, `/expected-returns`, `/mars-forecasts`.
 */
import { Suspense, lazy } from 'react';
import { createBrowserRouter } from 'react-router-dom';
import App from '@/App';
import { AuthProvider } from '@/lib/auth/AuthProvider';
import { RequireAuth } from '@/lib/auth/RequireAuth';
import { RequireFlag } from '@/lib/auth/RequireFlag';
import { Loader } from '@/components/Loader';
import { MarsForecastsRoute } from '@/MarsForecastsRoute';
import LoginPage from '@/features/auth/LoginPage';
import LoggedOutPage from '@/features/auth/LoggedOutPage';
import DisclaimerPage from '@/features/static/DisclaimerPage';
import ErrorPage from '@/features/error/ErrorPage';
import NotFoundPage from '@/features/error/NotFoundPage';
import DashboardPage from '@/features/dashboard/DashboardPage';
import MarsPortfolioPage from '@/features/mars/portfolio/MarsPortfolioPage';
import EconomicOutlookPage from '@/features/economic-outlook/EconomicOutlookPage';
import LoginAnalyticsPage from '@/features/admin/LoginAnalyticsPage';
import UserActivityPage from '@/features/admin/UserActivityPage';
import MarsLivePage from '@/features/mars/live/MarsLivePage';

// Plotly-heavy central-bank pages — lazy-loaded so Plotly stays in its own
// chunk (vite.config.ts manualChunks) instead of the main bundle. The
// landing/dashboard route stays eager: lazy-loading the first route hit
// only adds a fallback flash. Same pattern as MarsForecastsRoute.
const TimeseriesPage = lazy(() => import('@/features/central-bank/timeseries/TimeseriesPage'));
const SpeechesPage = lazy(() => import('@/features/central-bank/speeches/SpeechesPage'));
const VotingDualPage = lazy(() => import('@/features/central-bank/voting/VotingDualPage'));

/** Wrap a lazy page element in the shared Suspense fallback. */
function lazyRoute(node: JSX.Element): JSX.Element {
  return <Suspense fallback={<Loader label="Loading" />}>{node}</Suspense>;
}

export const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthProvider>
        <App />
      </AuthProvider>
    ),
    children: [
      { index: true, element: <RequireAuth><DashboardPage /></RequireAuth> },
      { path: 'economic-outlook', element: <RequireAuth><EconomicOutlookPage /></RequireAuth> },
      { path: 'econ-activity', element: <RequireAuth><EconomicOutlookPage /></RequireAuth> },
      { path: 'central-bank', element: <RequireAuth>{lazyRoute(<SpeechesPage />)}</RequireAuth> },
      {
        path: 'central-bank/timeseries',
        element: <RequireAuth>{lazyRoute(<TimeseriesPage />)}</RequireAuth>,
      },
      {
        path: 'central-bank/voting-analysis',
        element: <RequireAuth>{lazyRoute(<VotingDualPage />)}</RequireAuth>,
      },
      { path: 'forecasts', element: <MarsForecastsRoute /> },
      { path: 'forecasts/:tab', element: <MarsForecastsRoute /> },
      { path: 'expected-returns', element: <MarsForecastsRoute /> },
      { path: 'mars-forecasts', element: <MarsForecastsRoute /> },
      { path: 'mars-portfolio-dashboard', element: <RequireAuth><MarsPortfolioPage /></RequireAuth> },
      {
        path: 'mars-live',
        element: (
          <RequireFlag
            flag="isFulcrumInternal"
            deniedTitle="Fulcrum internal only"
            deniedMessage="This feature is restricted to Fulcrum internal users."
          >
            <MarsLivePage />
          </RequireFlag>
        ),
      },
      {
        path: 'admin/login-analytics',
        element: (
          <RequireFlag
            flag="isAnalyticsAdmin"
            deniedTitle="Access restricted"
            deniedMessage="This page is only available to analytics administrators. Contact your administrator if you believe this is a mistake."
          >
            <LoginAnalyticsPage />
          </RequireFlag>
        ),
      },
      {
        path: 'admin/user-activity',
        element: (
          <RequireFlag
            flag="isAnalyticsAdmin"
            deniedTitle="Access restricted"
            deniedMessage="This page is only available to analytics administrators. Contact your administrator if you believe this is a mistake."
          >
            <UserActivityPage />
          </RequireFlag>
        ),
      },
      { path: 'disclaimer', element: <RequireAuth><DisclaimerPage /></RequireAuth> },
      { path: 'error', element: <ErrorPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  {
    path: '/login',
    element: (
      <AuthProvider>
        <LoginPage />
      </AuthProvider>
    ),
  },
  {
    path: '/logout',
    element: (
      <AuthProvider>
        <LoggedOutPage />
      </AuthProvider>
    ),
  },
]);
